import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Input } from '../components/Input';

interface ApiKeyRow {
  provider: string;
  label: string;
  unlocks: string;
  fallback: string;
  placeholder: string;
  docsUrl: string;
  hasKey: boolean;
  masked: string | null;
  updatedAt: string | null;
}

const Page = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 880px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.5;
`;

const Notice = styled.div`
  margin: ${({ theme }) => theme.spacing.lg} 0;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div<{ $connected: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ $connected, theme }) => ($connected ? '#10B981' : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ProviderName = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatusPill = styled.span<{ $connected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $connected, theme }) => ($connected ? '#D1FAE5' : theme.colors.surfaceHover)};
  color: ${({ $connected, theme }) => ($connected ? '#065F46' : theme.colors.textSecondary)};
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const RowText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const Masked = styled.code`
  font-family: monospace;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  padding: 2px 8px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text};
`;

const InputRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const FlexInput = styled(Input)`
  flex: 1;
  font-family: monospace;
  font-size: 13px;
`;

const DocsLink = styled.a`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const ErrorText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.error};
`;

const SuccessText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #065F46;
`;

interface ProviderCardProps {
  row: ApiKeyRow;
  onChange: (row: ApiKeyRow) => void;
}

function ProviderCard({ row, onChange }: ProviderCardProps) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const save = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const updated = await apiClient.put<ApiKeyRow>(
        `/api/me/api-keys/${row.provider}`,
        { apiKey: draft.trim() },
      );
      onChange({ ...row, ...updated });
      setDraft('');
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!confirm(`Remove your ${row.label} key? You can paste a new one any time.`)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const updated = await apiClient.delete<ApiKeyRow>(`/api/me/api-keys/${row.provider}`);
      onChange({ ...row, ...updated });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card $connected={row.hasKey}>
      <CardHead>
        <ProviderName>{row.label}</ProviderName>
        <StatusPill $connected={row.hasKey}>
          {row.hasKey ? '● Connected' : '○ Using fallback'}
        </StatusPill>
      </CardHead>

      <Row>
        <RowLabel>Unlocks</RowLabel>
        <RowText>{row.unlocks}</RowText>
      </Row>

      <Row>
        <RowLabel>Fallback when no key</RowLabel>
        <RowText>{row.fallback}</RowText>
      </Row>

      {row.hasKey && row.masked && (
        <Row>
          <RowLabel>Current</RowLabel>
          <RowText>
            <Masked>{row.masked}</Masked>
            {row.updatedAt && (
              <span style={{ marginLeft: 8, color: '#94A3B8', fontSize: 12 }}>
                · updated {new Date(row.updatedAt).toLocaleDateString()}
              </span>
            )}
          </RowText>
        </Row>
      )}

      <InputRow>
        <FlexInput
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={row.hasKey ? 'Paste a new key to replace' : row.placeholder}
          autoComplete="off"
        />
        <Button onClick={save} disabled={!draft.trim() || busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {row.hasKey && (
          <Button $variant="outline" onClick={clear} disabled={busy}>
            Remove
          </Button>
        )}
      </InputRow>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <DocsLink href={row.docsUrl} target="_blank" rel="noreferrer">
          Get a {row.label} key →
        </DocsLink>
        {error && <ErrorText>{error}</ErrorText>}
        {success && <SuccessText>{success}</SuccessText>}
      </div>
    </Card>
  );
}

export function ApiKeysPage() {
  const [rows, setRows] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiClient.get<{ keys: ApiKeyRow[] }>('/api/me/api-keys');
        setRows(data.keys);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load API keys');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateRow = (updated: ApiKeyRow) => {
    setRows((prev) => prev.map((r) => (r.provider === updated.provider ? updated : r)));
  };

  return (
    <Page>
      <Header>
        <Title>API Keys (optional)</Title>
        <Subtitle>
          Your subscription already includes all of the platform's AIs — you don't need to bring
          your own keys. This section is optional: only if you prefer to use your own keys
          (for example for your own quota), paste them here.
        </Subtitle>
      </Header>

      <Notice>
        🔐 Your keys are yours. They're stored in your account and only used for the requests
        you make. We don't share them with other users and you can remove them at any
        time — when you do, the platform goes back to using the keys included in your plan.
      </Notice>

      {loading && <RowText>Loading…</RowText>}
      {error && <ErrorText>{error}</ErrorText>}

      <List>
        {rows.map((row) => (
          <ProviderCard key={row.provider} row={row} onChange={updateRow} />
        ))}
      </List>
    </Page>
  );
}
